import localforage from 'localforage'
import * as z from 'zod'

import Llavero from '@comun/llavero'
import { ErrorAumentado } from '@comun/errorAumentado'
import type { AxiosRequestConfig } from 'axios'
import type { ResultadoDeSolicitudApi } from './tiposApi'

import { HashJson } from '@comun/hashU'
import { AppConfigFrontZod } from '@comun/appsConfigs'
import { ErrorNotificableZod, i18nIconos, i18nTapi } from '@base/lib/erroresNotificables'
import { getConfigNotificaciones } from '@base/lib/notificaciones/config'
import { EventosDeAppAPI } from './eventosDeApp'

// * Endpoints donde rotarLlavero + reintento automático es idempotente y seguro
const URLS_SEGURAS_PARA_REINTENTO = [
  '/llaveros/registrarLlavero',
  '/llaveros/validarLlavero',
  '/notificador/suscribir'
]

function esUrlSeguraParaReintentar(url: string): boolean {
  return URLS_SEGURAS_PARA_REINTENTO.some(ruta => url.endsWith(ruta))
}

// * Almacenamiento que dura lo que dure la instalación en nativos. En web lo que dure la caché.
const llaveroStoreLF = localforage.createInstance({ name: 'llaveroStore' })

// * Helpers para serializar/deserializar llavero (para DatosPersistentes/Keychain)
async function serializarLlavero(llavero: Llavero): Promise<string> {
  const data = await llavero.serializar()
  return JSON.stringify(data)
}

async function deserializarLlavero(json: string): Promise<Llavero> {
  const data = JSON.parse(json)
  return Llavero.deserializar(data)
}

// * Wrapper que guarda en localforage (primario) y DatosPersistentesSeguro (Capa 1 — no respaldable, device-only)
const llaveroStore = {
  async getItemLlavero(clave: 'llaveroCliente' | 'llaveroServidor'): Promise<Llavero | null> {
    const valorLF = await llaveroStoreLF.getItem<Record<string, unknown>>(clave)
    if (valorLF) {
      // Formato nuevo: objeto plano con tipoLlavero
      if (typeof valorLF.tipoLlavero === 'string' && typeof valorLF.firmaPublica === 'string') {
        return Llavero.deserializar(valorLF as Parameters<typeof Llavero.deserializar>[0])
      }
      // Legacy: instancia de Llavero guardada directamente (RSA sin tipoLlavero)
      // Nota: CryptoKey no es JSON-serializable; al recuperar de localforage pueden ser objetos vacíos
      const legacy = valorLF as Record<string, unknown>
      const firmaPublica = (legacy.firma as Record<string, unknown> | undefined)?.publica
      const encPublica = (legacy.encriptacion as Record<string, unknown> | undefined)?.publica
      if (typeof legacy.llaveroID === 'string' && typeof firmaPublica === 'string' && typeof encPublica === 'string') {
        return Llavero.reinstanciar({
          firma: legacy.firma as { publica: string; privada?: string },
          encriptacion: legacy.encriptacion as { publica: string; privada?: string }
        })
      }
      if (typeof legacy.llaveroID === 'string') {
        consolo.warn('[llaveroStore] Datos legacy corruptos en localforage (CryptoKey no serializable), limpiando...')
        await llaveroStoreLF.removeItem(clave)
      }
    }
    const valorDP = await DatosPersistentesSeguro.leer('llavero_' + clave)
    if (!valorDP) return null
    return deserializarLlavero(valorDP)
  },

  async getItemRegistroID(): Promise<string | null> {
    const valorLF = await llaveroStoreLF.getItem<string>('registroID')
    if (valorLF) return valorLF
    return DatosPersistentesSeguro.leer('llavero_registroID')
  },

  async setItemLlavero(clave: 'llaveroCliente' | 'llaveroServidor', valor: Llavero): Promise<void> {
    const serializado = await valor.serializar()
    await llaveroStoreLF.setItem(clave, serializado)
    const stringificado = await serializarLlavero(valor)
    await DatosPersistentesSeguro.guardar('llavero_' + clave, stringificado)
  },

  async setItemRegistroID(valor: string): Promise<void> {
    await llaveroStoreLF.setItem<string>('registroID', valor)
    await DatosPersistentesSeguro.guardar('llavero_registroID', valor)
  },

  async removeItem(clave: 'llaveroCliente' | 'llaveroServidor' | 'registroID'): Promise<void> {
    await llaveroStoreLF.removeItem(clave)
    await DatosPersistentesSeguro.borrar('llavero_' + clave)
  }
}


async function fetchComoAxios({
  url,
  method,
  headers,
  data
}: {
  url: AxiosRequestConfig['url']
  method: AxiosRequestConfig['method']
  headers: AxiosRequestConfig['headers']
  data: AxiosRequestConfig['data']
}): Promise<unknown> {
  const fx = 'fetchComoAxios'
  consolo.debug('[llavero-cliente]', { url, method })
  try {
    if (!url) {
      throw new Error('URL is required');
    }

    const fetchConfig: RequestInit = {
      method: method?.toUpperCase() || 'GET',
      headers: new Headers()
    }

    if (headers) {
      for (const [key, value] of Object.entries(headers)) {
        (fetchConfig.headers as Headers).append(key, value); // Type assertion 
      }
    }

    if (data) {
      // Convert data to JSON before assigning to body
      fetchConfig.body = JSON.stringify(data);
      (fetchConfig.headers as Headers).append('Content-Type', 'application/json')
    }

    const response = await fetch(url, fetchConfig)
      .catch((error) => {
        consolo.error(fx, 'error', error)
        if (error instanceof Error) {
          const { message, stack } = error
          consolo.debug(fx, 'instanceof Error', { message, stack })

          const errorIDparseo = ErrorNotificableZod.safeParse(message)
          const errorNotificableID = errorIDparseo.success ? errorIDparseo.data : undefined
          if (errorNotificableID) NotificacionesEnApp.atencion({
            titulo: "Ups!",
            texto: i18nTapi(errorNotificableID),
            codigo: errorNotificableID,
            icono: i18nIconos[errorNotificableID],
            duracionSegundos: 20
          })

          throw new Error(message)
        }
        throw error
      });

    const bodyRespuesta = await response.json();
    const headersRespuesta: Partial<Headers> = lodash.pickBy(response.headers, (value) => typeof value === 'string');

    if (!bodyRespuesta.ok) {

      const comoEnAxios: ResultadoDeSolicitudApi = {
        success: true,
        data: bodyRespuesta,
        status: response.status,
        statusText: response.statusText,
        headers: headersRespuesta
      };
      consolo.error(fx, 'Error en respuesta:', bodyRespuesta.error)

      const error = typeof bodyRespuesta.error === 'string' ? bodyRespuesta.error : null
      const errorIDparseo = ErrorNotificableZod.safeParse(error)

      // Verificar si el servidor solicita rotar el llavero
      if (bodyRespuesta.rotarLlavero === true) {
        consolo.log('[llavero-cliente] Servidor solicita rotar llavero, rotando...')
        await LlaverosAPI.RotarLlavero()

        if (url && esUrlSeguraParaReintentar(url)) {
          consolo.log('[llavero-cliente] URL segura para reintento, reintentando:', url)
          return await fetchComoAxios({ url, method, headers, data })
        }

        consolo.warn('[llavero-cliente] URL no segura para reintento automático, emitiendo evento y fallando:', url)
        EventosDeAppAPI.emitirEventoDeApp('llavero:rotado', 'fetchComoAxios')
        throw new Error('llaveroRotado: reintento no seguro para ' + url)
      }

      const ERRORES_SILENCIOSOS = ['llaveroNoEncontrado']
      if (errorIDparseo.success && !ERRORES_SILENCIOSOS.includes(errorIDparseo.data)) {
        // Es un error notificable conocido
        const errorNotificableID = errorIDparseo.data
        NotificacionesEnApp.atencion({
          titulo: "Ups!",
          texto: i18nTapi(errorNotificableID),
          codigo: errorNotificableID,
          icono: i18nIconos[errorNotificableID]
        })
      }

      ManejadorErroresAxios(comoEnAxios)
    }

    IntegrarElementosFrescos({
      url,
      fetchConfig,
      dataRecibida: bodyRespuesta
    })

    return bodyRespuesta;
  } catch (error) {
    consolo.error(fx, 'catch', error);
    consolo.debug('JSON error', JSON.stringify(error));


    const errorString = error instanceof Error ? error.message : 'Unknown error';
    return {
      ok: false,
      error: errorString
    };
  }
}

type LlaverosYRegistro = {
  llaveroCliente: Llavero,
  llaveroServidor: Llavero,
  registroID: string
}

let llaverosYRegistro: LlaverosYRegistro | undefined = undefined

async function ObtenerLlaverosRegistrados(): Promise<LlaverosYRegistro> {
  const fx = 'Base.LlaverosAPI.ObtenerLlaverosRegistrados'
  try {
    if (llaverosYRegistro) return llaverosYRegistro

    const llaveroClienteEnStore = await llaveroStore.getItemLlavero('llaveroCliente')
    const llaveroServidorEnStore = await llaveroStore.getItemLlavero('llaveroServidor')
    const registroIDEnStore = await llaveroStore.getItemRegistroID()

    if (llaveroClienteEnStore && llaveroServidorEnStore && registroIDEnStore) {
      consolo.log(fx, 'Registro previo encontrado en store, saltando registro con servidor')
      llaverosYRegistro = { llaveroCliente: llaveroClienteEnStore, llaveroServidor: llaveroServidorEnStore, registroID: registroIDEnStore }
      return llaverosYRegistro
    }

    const registroID = registroIDEnStore ?? 'nuevo'

    // Generar llavero ed25519 (nueva generación por defecto)
    const llaveroCliente = llaveroClienteEnStore ?? await Llavero.generar('ed25519')
    if (!llaveroClienteEnStore) {
      consolo.log(fx, 'Generando nuevo llavero cliente Ed25519')
      await llaveroStore.setItemLlavero('llaveroCliente', llaveroCliente)
    }

    const { firma, encriptacion } = await llaveroCliente.exportarLlavesPublicas()
    const data = { firma, encriptacion }

    const hash = await HashJson(data)
    const signatura = await llaveroCliente.firmar({ hash, iss: llaveroCliente.llaveroID, rid: registroID })

    const headers = await HeadersBase()
    headers['signatura'] = signatura

    const respuestaRegistro = await fetchComoAxios({
      url: `${getConfigNotificaciones().apiUrl}/llaveros/registrarLlavero`,
      method: 'post',
      data,
      headers
    })

    const parseoRespuestaRegistro = z.object({
      ok: z.boolean(),
      firma: z.string(),
      encriptacion: z.string(),
      registroID: z.string(),
      desafioAFirmar: z.string()
    }).safeParse(respuestaRegistro)

    if (!parseoRespuestaRegistro.success) {
      throw new ErrorAumentado(fx, { error: 'falloParseoRespuestaRegistro' })
    }

    const respuestaValidada = parseoRespuestaRegistro.data

    await llaveroStore.setItemRegistroID(respuestaValidada.registroID)

    const llaveroServidor = await Llavero.reinstanciar({
      firma: { publica: respuestaValidada.firma },
      encriptacion: { publica: respuestaValidada.encriptacion }
    })

    await llaveroStore.setItemLlavero('llaveroServidor', llaveroServidor)

    // Validar desafío con el mismo llavero usado para registrar
    const esteData = { desafioFirmado: respuestaValidada.desafioAFirmar }
    const hashDesafio = await HashJson(esteData)
    const estaSignatura = await llaveroCliente.firmar({ hash: hashDesafio, iss: llaveroCliente.llaveroID, rid: respuestaValidada.registroID })

    const estosHeaders = await HeadersBase()
    estosHeaders['signatura'] = estaSignatura

    const respuestaValidacion = await fetchComoAxios({
      url: `${getConfigNotificaciones().apiUrl}/llaveros/validarLlavero`,
      method: 'post',
      data: esteData,
      headers: estosHeaders
    })

    const parseo = z.object({
      ok: z.boolean(),
      llaveroID: z.string(),
      registroID: z.string(),
      app: AppConfigFrontZod
    }).safeParse(respuestaValidacion)

    if (!parseo.success)
      throw new ErrorAumentado(fx, { error: parseo.error })

    const app = parseo.data.app
    if (app) { ActualizarAppConfig(app) }

    llaverosYRegistro = { llaveroCliente, llaveroServidor, registroID: respuestaValidada.registroID }
    return llaverosYRegistro
  }
  catch (e) {
    if (e instanceof ErrorAumentado) throw e.trazar(fx)
    throw new ErrorAumentado(fx, { error: e })
  }
}


let obtencionEnEjecucion: Promise<LlaverosYRegistro> | undefined = undefined


export const LlaverosAPI = {
  ObtenerLlaveros: async function (): Promise<LlaverosYRegistro> {
    const fx = 'Base.LlaverosAPI.ObtenerLlaveros'
    consolo.debug('[llavero-cliente]', 'obtencionEnEjecucion', obtencionEnEjecucion)
    try {
      if (obtencionEnEjecucion) return await obtencionEnEjecucion

      obtencionEnEjecucion = ObtenerLlaverosRegistrados()

      const resultado = await obtencionEnEjecucion
      obtencionEnEjecucion = undefined
      return resultado
    }
    catch (e) {
      consolo.error(fx, e)
      obtencionEnEjecucion = undefined
      if (e instanceof ErrorAumentado)
        throw e.trazar(fx)
      throw new ErrorAumentado(fx, { error: e })
    } finally {
      obtencionEnEjecucion = undefined
    }
  },
  RotarLlavero: async function (): Promise<void> {
    const fx = 'RotarLlavero'
    consolo.log(fx)
    try {
      await llaveroStore.removeItem('llaveroCliente')
      await llaveroStore.removeItem('llaveroServidor')
      await llaveroStore.removeItem('registroID')
      llaverosYRegistro = undefined

      consolo.log(fx, 'llavero cliente eliminado')
      await LlaverosAPI.ObtenerLlaveros()

      const tokenActual = await DatosPersistentesPersonal.leer('token')
      if (tokenActual) {
        consolo.log(fx, 'Token encontrado, refrescando usuario...')
        await UsuarioAPI.Refrescar()
      }
    }
    catch (e) {
      if (e instanceof ErrorAumentado) throw e.trazar(fx)
      throw new ErrorAumentado(fx, { error: e })
    }
  },
  RotarRegistro: async function (): Promise<void> {
    const fx = 'RotarRegistro'
    consolo.debug(fx)
    try {
      await llaveroStore.removeItem('registroID')
      llaverosYRegistro = undefined

      consolo.debug(fx, 'llavero cliente eliminado')
      await LlaverosAPI.ObtenerLlaveros()

      const tokenActual = await DatosPersistentesPersonal.leer('token')
      if (tokenActual) {
        consolo.log(fx, 'Token encontrado, refrescando usuario...')
        await UsuarioAPI.Refrescar()
      }
    }
    catch (e) {
      if (e instanceof ErrorAumentado) throw e.trazar(fx)
      throw new ErrorAumentado(fx, { error: e })
    }
  }
}

